import { Badge, Button, Card, Divider, Modal, Tooltip, message } from "antd";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import {
  SettingOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Meta from "antd/es/card/Meta";
import styles from "./LicenseCard.module.scss";
import {
  IoCalendarNumber,
  IoCheckmarkCircleOutline,
  IoWarningOutline,
} from "react-icons/io5";
import meetingCardImg from "../../Image/meeting-card-img.png";
import virtualOfficeImg from "../../Image/virtual-office.png";
import voiceCardImg from "../../Image/voicemail-card-img.png";
import smsImg from "../../Image/sms-card-img.png";
import aiUsersImg from "../../Image/ai-users-card-img.png";
import wilineLogo from "../../Image/logo-blue.png";
import LicenseEditor from "../LicenseSettings/LicenseEditor";
import colors from "../../Utils/colors";
import { formatDate } from "../../Utils/Utils";
import { useLicenseSettings } from "../../Components/LicenseSettings/LicenseSettingsContext";
import LicenseOverviewFunction from "../../Functions/LicenseOverview/LicenseOverview";
import { CustomerLicense } from "../../Utils/types";
import { showContactUsPage } from "../../Components/LicenseSettings/modals/modals";
import LicenseTransferModal from "../../Components/LicenseSettings/LicenseTransferModal";
import { hasFullLicenseOfSameType } from "../../Components/LicenseSettings/utils/LicenseManipulation";
import { isAiUsersLicense } from "../../Components/LicenseSettings/utils/licenseType";

interface LicenceCardProps {
  license: CustomerLicense;
  onLicenseDelete: () => void;
  cardConent?: React.ReactNode;
  cardActions?: React.ReactNode[];
  style?: React.CSSProperties;
  type: string;
}

// icon map for avatar picture rendering

interface IconMap {
  [key: string]: string;
}

const iconMap: IconMap = {
  MeetingLicense: meetingCardImg,
  Voicemail: voiceCardImg,
  SMSLicense: smsImg,
  VirtualOffice: virtualOfficeImg,
  AIUsers: aiUsersImg,
};

const dateOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const LicenseCard: React.FC<LicenceCardProps> = ({
  license,
  onLicenseDelete,
  cardActions: customCardActions,
  cardConent: customCardContent,
  style,
  type,
}) => {
  const context = useLicenseSettings();
  const customerLicense = useSelector(
    (state: RootState) => state.user.licenseTypes
  );
  const [transferModalVisible, setTransferModalVisible] = React.useState(false);
  const destinationLicense = hasFullLicenseOfSameType(customerLicense, license);
  const showTransferDialog = useMemo(
    () => destinationLicense && context.totalValue > 0,
    [context.totalValue]
  );
  const finalExpireDate = useMemo(
    () =>
      license.lastOffDate
        ? new Date(license.lastOffDate).toLocaleString("en-US", dateOptions)
        : null,
    [license.lastOffDate]
  );
  const finalAssignDate = useMemo(
    () =>
      license.assignDate
        ? new Date(license.assignDate).toLocaleString("en-US", dateOptions)
        : "",
    [license.assignDate]
  );

  const willExpireSoon = useMemo(
    () =>
      license.lastOffDate
        ? new Date(license.lastOffDate).getTime() - Date.now() <
          7 * 24 * 60 * 60 * 1000
        : false,
    [license.lastOffDate]
  );
  const accesstoken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const sessionToken = useSelector((state: RootState) => state.user.token);

  const userData = useSelector((state: RootState) => state.user);

  const { totalValue, trialStatus } = useLicenseSettings();

  const { toggleVisible } = useLicenseSettings();

  const licenseImg = useMemo(() => {
    if (isAiUsersLicense(type)) {
      return aiUsersImg;
    }
    const normalizedType = type.replace(/\s/g, "");
    return iconMap[normalizedType] ?? wilineLogo;
  }, [type]);

  const getBadgeIcon = (trialStatus: boolean | undefined, cgsStatus: string) => {
    if (trialStatus) {
      return <IoCalendarNumber style={{ color: "#f5222d" }} />;
    }

    if (cgsStatus === "Active") {
      return <IoCheckmarkCircleOutline style={{ color: "green" }} />;
    }

    return <IoWarningOutline style={{ color: "orange" }} />;
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this license?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      maskClosable: true,
      cancelText: "No",
      onOk() {
        return deleteLicense(id);
      },
    });
  };

  const deleteLicense = async (id: number) => {

    if (!accesstoken || !sessionToken) {
      return;
    }

    try {
      const response = await LicenseOverviewFunction.removeCustomerLicense(
        accesstoken,
        sessionToken,
        id
      );

      if (response.status) {
        message.success("License deleted successfully", 5);
        onLicenseDelete();
      } else {
        message.error("Error deleting license");
      }
    } catch (error) {
      console.log("Error deleting license: ", error);
      message.error("Error deleting license");
    }
  };

  const defaultCardActions = useMemo(
    () =>
      [
        license.value !== "unlimited" && (
          <SettingOutlined
            key="setting"
            onClick={() => !customCardActions && toggleVisible(true)}
          />
        ),
        showTransferDialog ? (
          <Tooltip title="Transfer users to full license">
            <Button
              key={"transfer-" + license.id}
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                setTransferModalVisible(true);
              }}
            >
              Transfer
            </Button>
          </Tooltip>
        ) : (
          <Button
            key={"buy-" + license.id}
            type="primary"
            size="middle"
            onClick={(e) => {
              e.stopPropagation();
              showContactUsPage(license.type === "TRIAL");
            }}
          >
            {license.type === "TRIAL" ? "Activate" : "Buy More"}
          </Button>
        ),
        trialStatus && userData.user?.loginType === "super" && (
          <DeleteOutlined
            key="delete"
            style={{ color: "#FF0000" }}
            onClick={(e) => {
              e.stopPropagation();
              showDeleteConfirm(license.id);
            }}
          />
        ),
      ].filter(Boolean),
    [license.value, showTransferDialog]
  );

  const defaultCardContent = (
    <Card
      className={styles.cardStyle}
      actions={customCardActions || defaultCardActions}
      onClick={() =>
        license.value !== "unlimited" && !customCardActions
          ? toggleVisible(true)
          : undefined
      }
      style={style}
    >
      <Meta
        avatar={
          <div className={styles.avatarImgWrapper}>
            <img alt={type} src={licenseImg} className={styles.avatarImg} />
          </div>
        }
        title={type}
        description={`${type} plan details.`}
      />
      <Divider />
      <table className={styles.cardDetails}>
        <tbody>
          <tr>
            <td>
              <Badge
                style={{ marginLeft: "5px" }}
                key={"blue"}
                color={"blue"}
              />
            </td>
            <td>Number of licences:</td>
            <td style={{ textAlign: "right" }}>{license.value}</td>
          </tr>

          <tr>
            {license.value !== "unlimited" ? (
              <>
                <td>
                  <Badge
                    style={{ marginLeft: "5px" }}
                    key={"green"}
                    color={"green"}
                  />
                </td>
                <td>Licences used:</td>
                <td style={{ textAlign: "right" }}>{totalValue}</td>
              </>
            ) : (
              <td colSpan={3}>&nbsp;</td> // Empty row for alignment
            )}
          </tr>

          <tr>
            <td>
              <Badge
                style={{ marginRight: "5px" }}
                count={<IoCalendarNumber style={{ color: "green" }} />}
              />
            </td>
            <td> Assign date:</td>
            <td style={{ textAlign: "right" }}>
              {formatDate(finalAssignDate)}
            </td>
          </tr>
          <tr>
            <td>
              <Badge
                style={{ marginRight: "5px" }}
                count={getBadgeIcon(trialStatus, license.cgsStatus)}
              />
            </td>
            {trialStatus ? (
              <>
                <td> Expire date:</td>
                <td style={{ textAlign: "right" }}>
                  {willExpireSoon && (
                    <Tooltip title="License will expire in less then 7 days">
                      <ExclamationCircleOutlined
                        style={{ color: "red", paddingRight: "5px" }}
                      />
                    </Tooltip>
                  )}
                  {formatDate(finalExpireDate ?? undefined)}
                </td>
              </>
            ) : (
              <>
                <td> Status:</td>
                <td style={{ textAlign: "right" }}>{license.cgsStatus}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
      <br />
    </Card>
  );

  return (
    <>
      {showTransferDialog && destinationLicense && (
        <LicenseTransferModal
          visible={transferModalVisible}
          setModalVisible={setTransferModalVisible}
          customerLicenseId={license.id}
          transferToLicense={destinationLicense}
          licenseName={type}
        />
      )}
      <LicenseEditor licenseSettings={license} type={type} />
      {trialStatus ? (
        <Badge.Ribbon
          placement={"start"}
          color={colors.colorPrimaryWillineOrange}
          text="Trial"
        >
          {customCardContent || defaultCardContent}
        </Badge.Ribbon>
      ) : (
        customCardContent || defaultCardContent
      )}
    </>
  );
};

export default React.memo(LicenseCard);
